/**
 * components/ui/PhotoThumbPreview.tsx
 * ------------------------------------------------------------------
 * "HOW IT WILL LOOK" — a chosen photo shown, before it is sent, as the
 * portal will show it: one small square. (It showed three sizes at first;
 * they are all the same square scaled, so one says the same thing.)
 *
 * Every place a photo appears is a SQUARE cut from the MIDDLE of it, and the
 * server cuts the stored thumbnail the same way (functions/lib/
 * photoRedraw.js, position 'centre'), so what this shows is what appears.
 * A parent can see at once that a wide photo of the whole family, or one
 * where the child stands at the edge, will not make a usable picture —
 * and choose another before sending, not after.
 *
 * Used by the Parent Portal photo card and the admissions form (Oct 2026).
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { framingAdvice } from '@/lib/studentPhotoPolicy';

/** One square, at the profile size. Every place the portal shows a photo
 *  uses this same middle square, only scaled, so if it frames well here it
 *  frames well everywhere — one picture says it without an explanation. */
const PX = 64;

export default function PhotoThumbPreview({ src, name }: { src: string; name: string }) {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const advice = dims ? framingAdvice(dims.w, dims.h) : [];

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
      <div className="flex items-center gap-3">
        <img
          src={src}
          alt={`${name}, as the photo will appear`}
          onLoad={(e) => setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          style={{ width: PX, height: PX }}
          className="rounded-2xl object-cover object-center border border-gray-200 dark:border-gray-700 shrink-0"
        />
        <p className="text-[13px] text-gray-700 dark:text-gray-200">
          This is how the photo will appear on the portal. Is {name}’s face clear and in the middle?
        </p>
      </div>
      {advice.map((a) => (
        <p key={a} className="text-[12px] text-amber-700 dark:text-amber-300 mt-2 flex items-start gap-1.5">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {a}
        </p>
      ))}
    </div>
  );
}
